import { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";

const API_BASE_URL = 'https://barrack.ai';
const GPU_PRICING = {
    'H100-SXM5-80GB': {
        'price': {'USD': 3.59, 'EUR': 3.89, 'INR': 359},
    },
    'H100-PCIe-NVLink-80GB': {
        'price': {'USD': 3.19, 'EUR': 3.45, 'INR': 319},
    },
    'H100-PCIe-80GB': {
        'price': {'USD': 2.89, 'EUR': 3.09, 'INR': 289},
    },
    'A100-PCIe-80GB': {
        'price': {'USD': 1.49, 'EUR': 1.59, 'INR': 149},
    },
    'L40': {
        'price': {'USD': 1.19, 'EUR': 1.29, 'INR': 119},
    },
    'RTX-A6000': {
        'price': {'USD': 0.59, 'EUR': 0.65, 'INR': 59},
    },
    'B300-262GB': {
        'price': {'USD': 8.99, 'EUR': 9.65, 'INR': 899},
    },
    'B200-180GB': {
        'price': {'USD': 6.89, 'EUR': 7.39, 'INR': 689},
    },
    'H200-141GB': {
        'price': {'USD': 4.45, 'EUR': 4.79, 'INR': 445},
    },
    '1x B300 SXM6 262GB': {
        'price': {'USD': 8.99, 'EUR': 9.65, 'INR': 899},
    },
    'B300 SXM6 262GB': {
        'price': {'USD': 8.99, 'EUR': 9.65, 'INR': 899},
    },
    'B300': {
        'price': {'USD': 8.99, 'EUR': 9.65, 'INR': 899},
    },
    '1x H200 SXM5 141GB': {
        'price': {'USD': 4.45, 'EUR': 4.79, 'INR': 445},
    },
    'H200 SXM5 141GB': {
        'price': {'USD': 4.45, 'EUR': 4.79, 'INR': 445},
    },
    'H200': {
        'price': {'USD': 4.45, 'EUR': 4.79, 'INR': 445},
    },
    '1x B200 180GB': {
        'price': {'USD': 6.89, 'EUR': 7.39, 'INR': 689},
    },
    'B200 180GB': {
        'price': {'USD': 6.89, 'EUR': 7.39, 'INR': 689},
    },
    'B200': {
        'price': {'USD': 6.89, 'EUR': 7.39, 'INR': 689},
    },
};

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'dashboard' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'dashboard' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'dashboard',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

const currencyUtils = {
  getSymbol: (currencyCode) => {
    switch(currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      default: return '$'; // Default to USD symbol
    }
  },
  
  // Default currency for the application
  defaultCurrency: 'USD'
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  // Authentication & Authorization
  'AUTH_ERROR': 'Please log in again to continue',
  'ACCESS_DENIED': 'Access denied',
  'USER_NOT_FOUND': 'User profile not found',
  'SESSION_EXPIRED': 'Your session has expired. Please log in again',
  'METHOD_NOT_ALLOWED': 'Invalid request method',
  'USER_CREATION_FAILED': 'Failed to create user account. Please try again.',
  
  // Profile & User Management
  'PROFILE_FETCH_FAILED': 'Unable to load profile settings',
  'PROFILE_UPDATE_FAILED': 'Unable to update profile. Please try again.',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'INVALID_USER_PROFILE': 'Invalid user profile configuration',
  'INVALID_BARRACK_ID_FORMAT': 'Invalid user identifier format',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'REQUEST_TOO_LARGE': 'Request data is too large',
  'INVALID_CURRENCY': 'Invalid currency selection',
  'COUNTRY_REQUIRED': 'Country selection is required',

  
  // VM Operations
  'VM_DEPLOY_FAILED': 'Unable to deploy VM. Please try again.',
  'VM_STATUS_FETCH_FAILED': 'Unable to check VM status. Please try again.',
  'VM_NOT_FOUND_OR_FORBIDDEN': 'VM not found or access denied',
  'VM_NOT_FOUND': 'VM not found',
  'VM_NOT_ACTIVE': 'VM must be in active state for this operation',
  'VM_PERMISSION_DENIED': 'You do not have permission to perform this operation on this VM',
  'INVALID_VM_ID_FORMAT': 'Invalid VM identifier',
  'INVALID_VM_STATE': 'VM cannot perform this operation in current state',
  'VM_RESIZE_ERROR': 'Unable to resize VM. Please try again.',
  'VM_START_ERROR': 'Unable to start VM. Please try again.',
  'VM_STOP_ERROR': 'Unable to stop VM. Please try again.',
  'VM_REBOOT_ERROR': 'Unable to reboot VM. Please try again.',
  'VM_DELETE_ERROR': 'Unable to delete VM. Please try again.',
  'VM_DELETE_FAILED': 'VM deletion failed. Please try again.',
  'HIBERNATE_ERROR': 'Unable to hibernate VM. Please try again.',
  'RESTORE_ERROR': 'Unable to restore VM. Please try again.',
  'RESTORE_FAILED': 'VM restoration failed. Please try later.',
  'DELETE_ERROR': 'VM deletion encountered an error. Please try again.',
  'VM_DETAILS_ERROR': 'Unable to retrieve VM details. Please try again.',
  'INVALID_ID_FORMAT': 'Invalid identifier format',
  'REQUEST_TIMEOUT': 'Unable to retrieve details. Please try again in a moment.',
  'UNKNOWN_TIER': 'Unknown service tier',
  // In the ERROR_MESSAGES object, add these codes in appropriate sections:

// VM Operations (add to existing VM Operations section)
'VM_NOT_SHUTOFF': 'VM must be in SHUTOFF state for this operation',
'VM_LIST_FAILED': 'Unable to load virtual machines. Please try again.',
'UNSUPPORTED_OPERATION': 'This operation is not supported for this region/tier',
'REGION_MISMATCH': 'Operation requires resources in the same region',
'RESOURCE_IN_USE': 'This resource is currently in use and cannot be modified',

// Volume Status (create new section or add to Resource Management)
'VOLUME_STATUS_CHECK_FAILED': 'Unable to verify volume status. Please try again.',
'VOLUME_ATTACHED_TO_RUNNING_INSTANCE': 'Volume is attached to a running VM. Please shut down the VM first.',
'VOLUME_STATUS_INCONSISTENT': 'Volume status is inconsistent. Please wait and try again.',

// Images & Profile (add to existing Images/Profile sections)
'OS_IMAGES_FETCH_FAILED': 'Unable to load OS images. Please try again.',
'USER_AUTH_PROFILE_FAILED': 'Unable to load user profile. Please try again.',
'BOOTABLE_VOLUMES_NOT_SUPPORTED': 'Bootable volumes are not supported in this region',
  // Instance Security Rules
  'INVALID_INSTANCE_ID_FORMAT': 'Invalid instance identifier format',
  'INSTANCE_NOT_FOUND': 'Instance not found',
  'INSTANCE_NOT_ACTIVE': 'Instance must be in active state for this operation',
  'INVALID_IP_FORMAT': 'Invalid IP address format',
  'SECURITY_RULE_ALREADY_EXISTS': 'Security rule already exists',
  'UNEXPECTED_BODY': 'Unexpected request data',
  'INVALID_RULE_ID_FORMAT': 'Invalid rule identifier format',
  'RULE_NOT_FOUND': 'Security rule not found',
  
  // Firewall Operations
  'FIREWALL_NOT_FOUND': 'Firewall not found',
  'INVALID_REGION_FORMAT': 'Invalid region format',
  'UNSUPPORTED_REGION': 'Region is not supported',
  
  // Volume Operations
  'VOLUME_LIST_FAILED': 'Unable to load volumes. Please refresh the page.',
  'VOLUME_CREATE_FAILED': 'Unable to create volume. Please try again.',
  'VOLUME_SIZE_INVALID': 'Invalid volume size specified',
  'INVALID_VOLUME_TYPE': 'Invalid volume type specified',
  'REGION_NOT_SUPPORTED': 'Selected region is not supported',
  'FEATURE_NOT_SUPPORTED': 'This feature is not supported for your configuration',
  'INVALID_IMAGE_NAME': 'Invalid OS image name',
  
  // Snapshot Operations
  'SNAPSHOT_CREATE_FAILED': 'Unable to create snapshot. Please try again.',
  'SNAPSHOT_ALREADY_EXISTS': 'A snapshot with this name already exists',
  'INVALID_SNAPSHOT_PARAMETERS': 'Invalid snapshot configuration',
  'SNAPSHOT_IN_PROGRESS': 'A snapshot is already being created for this VM',
  'QUOTA_EXCEEDED': 'Snapshot quota exceeded. Please delete unused snapshots.',
  'INSUFFICIENT_STORAGE': 'Insufficient storage space for snapshot',
  'DUPLICATE_SNAPSHOT_NAME': 'Snapshot name already exists. Choose a different name.',
  'INVALID_SNAPSHOT_NAME': 'Invalid snapshot name format',
  'INVALID_API_RESPONSE': 'Invalid response from service',
  
  // Images
  'IMAGE_LIST_FAILED': 'Unable to load OS images',
  'IMAGE_API_ERROR': 'Unable to retrieve images from service',
  'INVALID_REGION_GPU_COMBINATION': 'Invalid region and GPU combination',
  
  // Financial & Credits
  'INSUFFICIENT_CREDITS': 'Insufficient credits for this operation',
  'PAYMENT_REQUIRED': 'Payment required to continue',
  'CURRENCY_CONTEXT_MISMATCH': 'Resource not available in current currency context',
  'DB_ERROR': 'Service temporarily unavailable',
  
  // Resource Management
  'RESOURCE_NOT_FOUND': 'Requested resource not found',
  'RESOURCE_ALREADY_EXISTS': 'Resource already exists',
  'FLAVORS_FETCH_FAILED': 'Unable to load GPU configurations',
  'STOCKS_FETCH_FAILED': 'Unable to load availability data',
  
  // Security & SSH
  'SSH_KEY_REQUIRED': 'SSH key is required for VM deployment',
  'SECURITY_RULE_APPLICATION_FAILED': 'Security rules could not be applied',
  'PERMISSION_DENIED': 'You do not have permission to perform this operation',
  
  // Validation & Input
  'VALIDATION_ERROR': 'Please check your input and try again',
  'INVALID_REQUEST': 'Invalid request format',
  'INVALID_PARAMETERS': 'Invalid request parameters',
  'MISSING_PARAMETER': 'Required information is missing',
  'MISSING_FIELDS': 'Required fields are missing',
  'INVALID_JSON': 'Invalid request format',
  'UNEXPECTED_PAYLOAD': 'Unexpected request data',
  
  // System & Network
  'SERVER_ERROR': 'Service temporarily unavailable',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable',
  'SERVICE_ERROR': 'Service temporarily unavailable',
  'SERVICE_MAINTENANCE': 'Service is under maintenance. Please try again later.',
  'DATABASE_ERROR': 'Service temporarily unavailable',
  'DATABASE_RETRY_FAILED': 'Database operation failed after multiple attempts',
  'NETWORK_ERROR': 'Connection error. Please check your internet.',
  'API_ERROR': 'Service temporarily unavailable. Please try again.',
  'API_CONNECTION_ERROR': 'Unable to connect to service. Please try again.',
  'LOGOUT_ERROR': 'An error occurred during logout',
  
  // Tier & Feature Support
  'TIER_NOT_SUPPORTED': 'This operation is not supported for your VM tier',
  
  // Public IP Operations
  'PUBLIC_IP_ALREADY_ATTACHED': 'VM already has a public IP attached',
  'NO_PUBLIC_IP_ATTACHED': 'VM does not have a public IP to detach',
  'PUBLIC_IP_OPERATION_FAILED': 'Public IP operation failed. Please try again.',
  'PUBLIC_IP_ERROR': 'Public IP operation encountered an error',
  
  // Generic fallback
  'UNKNOWN_ERROR': 'Something went wrong. Please try again.',
  // Network Errors (NEW)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Input validation functions
const validateJupyterPassword = (password) => {
  if (!password || typeof password !== 'string') return { valid: false, error: 'Password is required' };
  if (password.length < 8) return { valid: false, error: 'Password must be at least 8 characters' };
  if (password.length > 128) return { valid: false, error: 'Password must be 128 characters or less' };
  return { valid: true };
};

const validateCloudInitScript = (script) => {
  if (script && typeof script === 'string' && script.length > 16384) {
    return { valid: false, error: 'Cloud-init script must be 16KB or less' };
  }
  return { valid: true };
};

const validateSnapshotName = (name) => {
  if (!name || typeof name !== 'string') return { valid: false, error: 'Name is required' };
  
  // Strip whitespace like backend does
  name = name.trim();
  
  if (name.length < 3) return { valid: false, error: 'Name must be at least 3 characters' };
  if (name.length > 30) return { valid: false, error: 'Name must be 30 characters or less' };
  
  // Only allow letters, numbers, hyphens, and underscores (NO periods or spaces)
  if (!/^[a-zA-Z0-9_-]+$/.test(name)) return { valid: false, error: 'Name can only contain letters, numbers, hyphens, and underscores' };
  
  // Cannot start with hyphen or underscore
  if (name.startsWith('-') || name.startsWith('_')) return { valid: false, error: 'Name cannot start with a hyphen or underscore' };
  
  // Cannot end with hyphen or underscore
  if (name.endsWith('-') || name.endsWith('_')) return { valid: false, error: 'Name cannot end with a hyphen or underscore' };
  
  return { valid: true };
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Check specific codes FIRST
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Check HTTP status codes
  if (error.response?.status >= 500 || error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  if (error.response?.status === 429 || error.status === 429) {
    const retryAfter = error.response?.headers['retry-after'] || 60;
    return `Too many requests. Please wait ${retryAfter} seconds before trying again.`;
  }
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['SESSION_EXPIRED'];
  }
  if (error.response?.status === 402 || error.status === 402) {
    return ERROR_MESSAGES['INSUFFICIENT_CREDITS'];
  }
  
  // Generic fallbacks LAST
  if (error.userMessage) {
    return error.userMessage;
  }
  if (error.error) {
    return error.error;
  }
  
  return 'Something went wrong. Please try again.';
};

// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

// Security: Validate error object structure
const validateErrorObject = (error) => {
  if (!error || typeof error !== 'object') return false;
  return true;
};

// Responsive utilities
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getGridClasses = () => {
    if (isMobile) return 'grid grid-cols-1 gap-3';
    if (isTablet) return 'grid grid-cols-2 gap-4';
    return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
  };

  const getStatsCardClasses = () => {
    if (isMobile) return 'grid grid-cols-2 gap-2';
    if (isTablet) return 'grid grid-cols-4 gap-3';
    return 'grid grid-cols-1 md:grid-cols-4 gap-6';
  };

  return { isMobile, isTablet, getGridClasses, getStatsCardClasses };
};
// VM Operation Cache Helpers - for smooth navigation between pages
const cacheVMOperation = (vmId, status) => {
    try {
        localStorage.setItem('vm_op', JSON.stringify({
            vmId: sanitizeText(vmId),
            status: sanitizeText(status),
            timestamp: Date.now()
        }));
        logInfo('Cached VM operation', { vmId, status });
    } catch (e) {
        logError('Failed to cache VM operation', { vmId, status }, e);
    }
};

const getVMOperationCache = () => {
    try {
        const cached = localStorage.getItem('vm_op');
        if (!cached) return null;
        
        const { vmId, status, timestamp } = JSON.parse(cached);
        
        // Expire after 5 seconds
        if (Date.now() - timestamp > 5000) {
            localStorage.removeItem('vm_op');
            return null;
        }
        
        return { vmId, status };
    } catch (e) {
        localStorage.removeItem('vm_op');
        return null;
    }
};

const clearVMOperationCache = () => {
    try {
        localStorage.removeItem('vm_op');
        logInfo('Cleared VM operation cache');
    } catch (e) {
        // Silent fail
    }
};

export {
  API_BASE_URL,
  GPU_PRICING,
  logError,
  logInfo,
  logUserAction,
  logPerformance,
  currencyUtils,
  ERROR_MESSAGES,
  sanitizeText,
  validateJupyterPassword,
  validateCloudInitScript,
  validateSnapshotName,
  getSafeErrorMessage,
  validateErrorObject,
  useResponsive,
  cacheVMOperation,
  getVMOperationCache,
  clearVMOperationCache,
  getNetworkErrorMessage
};